/**
 * "Which reviews are waiting on me" — §7.2's question, as a screen.
 *
 * The list is `GET /api/approvals/mine`, which is every open request carrying a
 * reviewer row of this person's with no decision on it, filtered through
 * `permission_visible_nodes`. Two properties of that filter show up here:
 *
 * - **Required rows sort first.** The API orders by when the request was opened,
 *   which is the right default for a history and the wrong one for a queue: an
 *   advisory request from this morning would sit above a required one from last
 *   week that is blocking a publish. Reordering is presentation and stays here.
 * - **`unreachable` is rendered, not swallowed.** A required reviewer who has
 *   lost access to the document is a row nobody can clear, and it is exactly the
 *   thing that deadlocks a document. The count says it happened without naming
 *   the document the grants no longer reach.
 *
 * Titles are fetched per row against `GET /api/nodes/:id`, because the list
 * endpoint carries subject ids and no titles. That is an N+1 for a list that is
 * short by construction — it is the set of decisions one person owes — and every
 * row lands in the same cache entry the document screens read, so following one
 * costs nothing. The better fix is on the server; see the note in the review
 * report.
 */

import { useQueries } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { nodeQuery, useApprovalsAwaitingMe, useMe } from '../../api/queries.ts';
import type { ApprovalRequest, ApprovalReviewer } from '../../api/types.ts';
import { formatRelative } from '../../components/format.ts';
import { Alert, EmptyState, Loading, PageError } from '../../components/ui/feedback.tsx';
import { Card, Mono, PageHeader } from '../../components/ui/layout.tsx';
import { Table, TBody, TD, TH, THead, TR } from '../../components/ui/table.tsx';
import { ApprovalStatusBadge, RequirementBadge } from './vocabulary.tsx';

/** Everyone required who has not answered — what the document is actually waiting on. */
function outstandingRequired(request: ApprovalRequest): ApprovalReviewer[] {
  return request.reviewers.filter(
    (reviewer) => reviewer.requirement === 'required' && reviewer.decision === null,
  );
}

function headline(total: number, required: number): string {
  if (total === 0) return 'No review is waiting on you';
  const noun = total === 1 ? 'review is' : 'reviews are';
  if (required === 0) return `${total} advisory ${noun} waiting on you`;
  if (required === total) return `${total} ${noun} waiting on you, and blocking a publish`;
  return `${total} ${noun} waiting on you — ${required} of them blocking a publish`;
}

export function ApprovalsPage() {
  const me = useMe();
  const query = useApprovalsAwaitingMe();
  const viewerId = me.data?.user.id;

  const requests = query.data?.requests ?? [];
  const titles = useQueries({
    queries: requests.map((request) => nodeQuery(request.subject.id)),
    combine: (results) =>
      new Map(
        results.flatMap((result) =>
          result.data ? [[result.data.node.id, result.data.node.title] as const] : [],
        ),
      ),
  });

  if (query.isError) {
    return (
      <PageError
        error={query.error}
        title="Review waiting on you"
        back={{ href: '/', label: 'Overview' }}
      />
    );
  }

  // The requirement on *this* person's row, which is the only one that says
  // whether their silence is holding anything up.
  const mine = (request: ApprovalRequest) =>
    request.reviewers.find((reviewer) => reviewer.personId === viewerId);

  const sorted = [...requests].sort((a, b) => {
    const rank = (request: ApprovalRequest) => (mine(request)?.requirement === 'required' ? 0 : 1);
    return rank(a) - rank(b);
  });
  const requiredCount = sorted.filter(
    (request) => mine(request)?.requirement === 'required',
  ).length;

  return (
    <>
      <PageHeader
        title={query.isPending ? 'Review waiting on you' : headline(sorted.length, requiredCount)}
        description={
          <>
            §7.6 makes reviewers an explicit set, each flagged required or advisory. A required
            answer blocks publishing until it arrives; an advisory one never does, which is how
            &quot;legal should weigh in but isn&apos;t a gate&quot; works without a second
            mechanism.
          </>
        }
      />

      {query.data && query.data.unreachable > 0 ? (
        <Alert
          tone="warning"
          title={`${query.data.unreachable} more ${query.data.unreachable === 1 ? 'request names you and is' : 'requests name you and are'} out of your reach`}
        >
          You are still a reviewer on {query.data.unreachable === 1 ? 'it' : 'them'} and can no
          longer open the {query.data.unreachable === 1 ? 'document' : 'documents'}, so nothing you
          do here can clear those rows. If any of them is required, that document cannot publish
          until somebody restores your access or removes you from the request — it is a grant
          problem, and this is where it surfaces.
        </Alert>
      ) : null}

      <Card padded={false}>
        {query.isPending ? (
          <Loading />
        ) : sorted.length === 0 ? (
          <EmptyState
            title="Nothing is waiting on you"
            description="You are not an outstanding reviewer on any open approval request. This fills up when somebody asks for review on a document you can read."
          />
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>Document</TH>
                <TH>Your part</TH>
                <TH>Request</TH>
                <TH>Opened</TH>
                <TH>Still required</TH>
                <TH>{''}</TH>
              </TR>
            </THead>
            <TBody>
              {sorted.map((request) => {
                const self = mine(request);
                const blocking = outstandingRequired(request);
                return (
                  <TR key={request.id}>
                    <TD>
                      <Link
                        to="/documents/$nodeId"
                        params={{ nodeId: request.subject.id }}
                        className="font-medium text-primary no-underline hover:underline"
                      >
                        {titles.get(request.subject.id) ?? <Mono>{request.subject.id}</Mono>}
                      </Link>
                    </TD>
                    <TD>
                      {self ? (
                        <RequirementBadge requirement={self.requirement} />
                      ) : (
                        <span className="text-tertiary">—</span>
                      )}
                    </TD>
                    <TD>
                      <ApprovalStatusBadge status={request.status} />
                    </TD>
                    <TD className="text-xs text-tertiary">{formatRelative(request.createdAt)}</TD>
                    <TD>
                      {/* Named rather than counted: "waiting on 2 people" sends
                          somebody looking, and one of the two is usually them. */}
                      <span className="text-xs text-tertiary">
                        {blocking.map((reviewer) => reviewer.name ?? reviewer.personId).join(', ')}
                      </span>
                    </TD>
                    <TD>
                      <Link
                        to="/approvals/$requestId"
                        params={{ requestId: request.id }}
                        className="text-sm link"
                      >
                        Review it
                      </Link>
                    </TD>
                  </TR>
                );
              })}
            </TBody>
          </Table>
        )}
      </Card>
    </>
  );
}
